"use client";

import React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import logjs from "@metrojs/logjs";

import { useSyaryoDialog, CRITERIA_FIELDS } from "./useSyaryoDialog.js";

const log = new logjs("SyaryoDialog");

const IS_DEVELOPMENT = process.env.NODE_ENV === "development";

/**
 * 車輌検索ダイアログ（表示のみ。状態・操作は useSyaryoDialog）
 * @param {Object} props
 * @param {boolean} props.open
 * @param {() => void} props.onClose
 * @param {Object<string, string>} [props.initialCriteria] 親フォームから渡す初期条件（useSyaryoDialog の emptyCriteria 形。省略キーは空扱い）
 * @param {Function} [props.onApply] 行選択時（選択行オブジェクトを渡す）
 * @param {string} [props.title]
 * @param {boolean} [props.showSpecPaper] 仕様説明用の長文 Paper を出す（既定 true。埋め込み画面では false 推奨）
 * @param {number|string} [props.dialogSession] 開くたびに変えると initialCriteria を再取り込み
 * @param {string} [props.pcarSearchUrl] 省略時は既定プロキシ（`/api/syaryo/p-car` または `NEXT_PUBLIC_SYARYO_PCAR_URL`）。`""` でモックのみ
 * @param {boolean} [props.showPcarApiRawPanel] p_car 検索時、送信 POST 本文と API 生 JSON を一覧の下に表示（開発環境のみ。既定 true）
 */
export default function SyaryoDialog({
  open,
  onClose,
  initialCriteria = {},
  onApply,
  title = "車輌検索",
  showSpecPaper = false,
  dialogSession = 0,
  pcarSearchUrl,
  showPcarApiRawPanel = true,
}) {
  const showApiDebugPanel = IS_DEVELOPMENT && showPcarApiRawPanel;

  log.debug("START SyaryoDialog ------------", { open, dialogSession });

  const {
    criteria,
    rows,
    listColumns,
    handleCriteriaChange,
    handleSearch,
    handleClearCriteria,
    handleSelectRow,
    searchLoading,
    searchError,
    pcarStatusSummary,
    usePcar,
    pcarSearchUrlResolved,
    pcarLastFetchOk,
    lastPcarRawCount,
    pcarDebugBundle,
  } = useSyaryoDialog({
    open,
    onClose,
    initialCriteria,
    onApply,
    dialogSession,
    pcarSearchUrl,
  });

  const renderCriteriaField = (key, label, width = 120) => {
    const fieldSx = { width, minWidth: width, flexShrink: 0 };
    const value = criteria[key] ?? "";

    return (
      <TextField
        size="small"
        name={key}
        value={value}
        onChange={(e) => handleCriteriaChange(key, e.target.value)}
        placeholder={label}
        title={value || undefined}
        inputProps={{ "aria-label": label }}
        sx={fieldSx}
      />
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      scroll="paper"
      PaperProps={{
        sx: {
          m: { xs: 1, sm: 1.5 },
          width: "calc(100vw - 16px)",
          maxWidth: "min(1760px, calc(100vw - 16px))",
        },
      }}
    >
      <DialogTitle sx={{ pr: 6 }}>
        <Typography variant="h6" component="span">
          {title}
        </Typography>
        <IconButton aria-label="閉じる" onClick={onClose} sx={{ position: "absolute", right: 8, top: 8 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom textAlign="left">
              検索条件
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
              型式指定・類別・車輌型式・車台番号を条件に車輌情報を検索します。
            </Typography>
            <Box
              sx={{
                width: "100%",
                maxWidth: "100%",
                display: "flex",
                justifyContent: "center",
                overflowX: "auto",
                overflowY: "hidden",
                pb: 0.5,
                WebkitOverflowScrolling: "touch",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "row",
                  flexWrap: "wrap",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: { xs: 1.25, sm: 1.75 },
                  rowGap: 1.5,
                  verticalAlign: "top",
                }}
              >
              {CRITERIA_FIELDS.map(({ key, label, width }) => (
                <Box
                  key={key}
                  sx={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.875,
                    flex: "0 0 auto",
                  }}
                >
                  <Typography
                    component="span"
                    variant="body2"
                    sx={{
                      fontWeight: 600,
                      color: "text.secondary",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {label}
                  </Typography>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      flex: "0 0 auto",
                    }}
                  >
                    {renderCriteriaField(key, label, width)}
                  </Box>
                </Box>
              ))}
              </Box>
            </Box>
            <Box sx={{ mt: 2, display: "flex", justifyContent: "center", gap: 1, flexWrap: "wrap" }}>
              <Button variant="contained" onClick={handleSearch}>
                検索
              </Button>
              <Button variant="outlined" onClick={handleClearCriteria}>
                クリア
              </Button>
            </Box>
          </Paper>

          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
              検索結果一覧
            </Typography>
            {searchLoading ? <LinearProgress sx={{ mb: 1 }} /> : null}
            {searchError ? (
              <Alert severity="error" sx={{ mb: 1 }}>
                {searchError}
              </Alert>
            ) : null}
            {rows.length === 0 ? (
              <Typography color="text.secondary" component="div">
                {usePcar && pcarLastFetchOk && !searchError ? (
                  <>
                    条件に一致する車輌は見つかりませんでした。
                    {lastPcarRawCount === 0 ? (
                      <>
                        {" "}
                        車輌型式・型式指定・類別などを入力して、再度「検索」してください。
                      </>
                    ) : (
                      <> 検索条件を見直して、再度「検索」してください。</>
                    )}
                  </>
                ) : (
                  "該当する行がありません。"
                )}
              </Typography>
            ) : (
              <TableContainer sx={{ maxHeight: 360, overflowX: "auto", WebkitOverflowScrolling: "touch" }}>
                <Table size="small" stickyHeader sx={{ minWidth: "max-content", tableLayout: "auto" }}>
                  <TableHead>
                    <TableRow>
                      {listColumns.map((c) => (
                        <TableCell key={c.key} sx={{ whiteSpace: "nowrap" }}>
                          {c.label}
                        </TableCell>
                      ))}
                      <TableCell width={100} align="center" sx={{ whiteSpace: "nowrap" }}>
                        
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {rows.map((row, idx) => (
                      <TableRow
                        key={`${row.pcar?.KATA_SEQ ?? row.KATA_SEQ ?? ""}-${row.FULL_KATA_ER ?? row.carSharyoKatashiki ?? ""}-${idx}`}
                        hover
                      >
                        {listColumns.map((c) => (
                          <TableCell key={c.key} sx={{ whiteSpace: "nowrap" }}>
                            {row[c.key] ?? ""}
                          </TableCell>
                        ))}
                        <TableCell align="center" sx={{ whiteSpace: "nowrap" }}>
                          <Button size="small" variant="outlined" onClick={() => handleSelectRow(row)}>
                            選択
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>

          {showApiDebugPanel && usePcar && pcarDebugBundle ? (
            <Paper variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                API デバッグ（生 JSON）
              </Typography>
              {pcarStatusSummary ? (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {pcarStatusSummary}
                  {lastPcarRawCount != null ? ` / 応答 data 件数=${lastPcarRawCount}` : ""}
                </Typography>
              ) : null}
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1 }}>
                送信 POST 本文（psdt/p_car）
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontSize: 12,
                  overflow: "auto",
                  maxHeight: 120,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {pcarDebugBundle.postBody || "(なし)"}
              </Box>
              <Typography variant="body2" fontWeight="bold" sx={{ mt: 1.5 }}>
                API 応答 JSON（apiRaw）
              </Typography>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  mt: 0.5,
                  p: 1.5,
                  bgcolor: "grey.100",
                  borderRadius: 1,
                  fontSize: 11,
                  overflow: "auto",
                  maxHeight: 480,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-all",
                }}
              >
                {pcarDebugBundle.apiRaw != null
                  ? JSON.stringify(pcarDebugBundle.apiRaw, null, 2)
                  : "(なし)"}
              </Box>
              {rows.length > 0 ? (
                <>
                  <Typography variant="body2" fontWeight="bold" sx={{ mt: 1.5 }}>
                    マッピング後 1件目（車台番号確認用）
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                    MODEL_PLATE が null のときは API 応答に車台番号なし。検索条件の車台番号を一覧・選択時に補完します。
                  </Typography>
                  <Box
                    component="pre"
                    sx={{
                      m: 0,
                      mt: 0.5,
                      p: 1.5,
                      bgcolor: "grey.100",
                      borderRadius: 1,
                      fontSize: 11,
                      overflow: "auto",
                      maxHeight: 240,
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-all",
                    }}
                  >
                    {JSON.stringify(
                      {
                        carNumber: rows[0].carNumber,
                        pcar_MODEL_PLATE: rows[0].pcar?.MODEL_PLATE,
                        pcar_frameno: rows[0].pcar?.frameno,
                        pcar_FRAME_NO: rows[0].pcar?.FRAME_NO,
                        pcar: rows[0].pcar,
                      },
                      null,
                      2,
                    )}
                  </Box>
                </>
              ) : null}
            </Paper>
          ) : null}

        </Stack>
      </DialogContent>
    </Dialog>
  );
}
